/**
 * ConfirmDialog - reusable confirmation dialog built on the Dialog component.
 */
import type { ReactNode } from "react";
import { HelpCircle, Loader2, TriangleAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  confirmText?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  onConfirm: () => void;
  loading?: boolean;
  children?: ReactNode;
  /** Raise above Sheet panels (z-300) when confirming from a slide-over. */
  stackLayer?: "default" | "above-panels";
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  confirmText,
  cancelLabel = "Cancel",
  variant = "default",
  onConfirm,
  loading = false,
  children,
  stackLayer = "default",
}: ConfirmDialogProps) {
  const buttonText = confirmText || confirmLabel;
  const isDestructive = variant === "destructive";
  const Icon = isDestructive ? TriangleAlert : HelpCircle;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-0 overflow-hidden p-0 sm:max-w-md"
        stackLayer={stackLayer}
      >
        <div className="px-6 pt-6 pb-5 pr-14">
          <div className="flex gap-4 items-start">
            <div
              className={cn(
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
                isDestructive
                  ? "bg-[#ffe9e1] text-[#c4532f] dark:bg-[#ff8d6e]/15"
                  : "bg-[#e6f0ee] text-[#004952] dark:bg-primary/15 dark:text-primary",
              )}
            >
              <Icon className="h-5 w-5" />
            </div>
            <DialogHeader className="space-y-2 text-left">
              <DialogTitle className="text-lg font-semibold leading-snug tracking-tight text-foreground">
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>
          </div>

          {children && <div className="mt-4 pl-[3.75rem]">{children}</div>}
        </div>

        <DialogFooter className="mt-0 flex-row justify-end gap-2 border-t border-border px-6 py-4 sm:space-x-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="h-10 rounded-full px-5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              "flex h-10 min-w-24 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold text-white disabled:opacity-60",
              isDestructive ? "bg-[#c4532f] hover:bg-[#a8441f]" : "bg-[#004952] hover:bg-[#003a43]",
            )}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Please wait…
              </>
            ) : (
              buttonText
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
